import React, { useMemo } from 'react';
import {
  OPTICS_FRAMEWORK_2026,
  OPTICS_LENS_LABELS,
  type OpticsLens,
} from '../../types/optics2026';
import type { ReviewResponseEntry, SelfNarrative } from '../../types/magicLink';
import BottomSheet from '../ui/BottomSheet';
import Button from '../ui/Button';

/**
 * ConfirmReviewSheet — bottom-sheet shown before final submission.
 *
 * Fixes audit #2: submission is one-shot + locked, must confirm.
 * Fixes audit #14: dual progress for scores vs evidence so reviewer doesn't
 * think they're 100% done with evidence textareas all blank.
 *
 * Renders:
 *  - Dual progress (Scores: x/18 ✓ · Evidence: y/18 optional)
 *  - Reviewee context
 *  - 6 OPTICS value summary rows with Ind/Team/Co + average
 *  - Anonymity reminder (anonymous reviewers only)
 *  - Submit + Cancel footer
 */

export interface ConfirmReviewSheetProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  entries: Record<string, ReviewResponseEntry>;
  selfNarrative?: SelfNarrative;
  isSelf: boolean;
  /** True for self & line_manager — name will be shown to HR + line manager */
  isAttributed: boolean;
  revieweeName: string;
  /** "Peer reviewer" / "Line manager" / "Self-review" — for context strip */
  reviewerRoleLabel: string;
  submitting?: boolean;
  submitLabel?: string;
}

const LENS_KEYS: OpticsLens[] = ['ind', 'team', 'co'];

interface ValueSummary {
  code: string;
  title: string;
  filledLenses: number;
  totalLenses: number;
  avg: number | null;
  perLens: Record<OpticsLens, number | null>;
}

const ConfirmReviewSheet: React.FC<ConfirmReviewSheetProps> = ({
  open,
  onClose,
  onConfirm,
  entries,
  selfNarrative,
  isSelf,
  isAttributed,
  revieweeName,
  reviewerRoleLabel,
  submitting = false,
  submitLabel = 'Submit final review',
}) => {
  const summaries = useMemo<ValueSummary[]>(() => {
    return OPTICS_FRAMEWORK_2026.map(v => {
      const lensTotals: Record<OpticsLens, { sum: number; n: number }> = {
        ind: { sum: 0, n: 0 },
        team: { sum: 0, n: 0 },
        co: { sum: 0, n: 0 },
      };
      let filledLenses = 0;
      let scoreSum = 0;
      const totalLenses = v.areas.length * 3;

      for (const area of v.areas) {
        const entry = entries[area.id];
        if (!entry) continue;
        for (const lens of LENS_KEYS) {
          const s = entry.scores[lens];
          if (typeof s === 'number' && s > 0) {
            filledLenses++;
            scoreSum += s;
            lensTotals[lens].sum += s;
            lensTotals[lens].n += 1;
          }
        }
      }

      const perLens: Record<OpticsLens, number | null> = {
        ind: lensTotals.ind.n > 0 ? lensTotals.ind.sum / lensTotals.ind.n : null,
        team: lensTotals.team.n > 0 ? lensTotals.team.sum / lensTotals.team.n : null,
        co: lensTotals.co.n > 0 ? lensTotals.co.sum / lensTotals.co.n : null,
      };

      return {
        code: v.code,
        title: v.title,
        filledLenses,
        totalLenses,
        avg: filledLenses > 0 ? scoreSum / filledLenses : null,
        perLens,
      };
    });
  }, [entries]);

  const totalScores = useMemo(
    () => summaries.reduce((s, v) => s + v.totalLenses, 0),
    [summaries],
  );
  const filledScores = useMemo(
    () => summaries.reduce((s, v) => s + v.filledLenses, 0),
    [summaries],
  );

  const evidenceFields = useMemo(() => {
    let filled = 0;
    let total = 0;
    for (const v of OPTICS_FRAMEWORK_2026) {
      for (const a of v.areas) {
        total += 1;
        const entry = entries[a.id];
        if (entry?.evidence && entry.evidence.trim().length > 0) filled += 1;
      }
    }
    return { filled, total };
  }, [entries]);

  const narrativeFilled = useMemo(() => {
    if (!isSelf || !selfNarrative) return null;
    const keys = ['deliveredIndividually', 'supportedTeam', 'contributedToCompany', 'needSupport'] as const;
    return keys.filter(k => (selfNarrative[k] ?? '').trim().length > 0).length;
  }, [isSelf, selfNarrative]);

  const allScoresComplete = filledScores === totalScores;

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Review your answers"
      description="Submission is final and cannot be edited. Please verify before sending."
      footer={
        <div className="flex flex-col gap-2">
          <Button
            variant="submit"
            icon="check"
            fullWidth
            onClick={() => void onConfirm()}
            loading={submitting}
            disabled={!allScoresComplete}
          >
            {submitLabel}
          </Button>
          <Button variant="tertiary" fullWidth onClick={onClose} disabled={submitting}>
            Go back and edit
          </Button>
        </div>
      }
    >
      <div className="px-5 py-4 space-y-4">
        {/* Dual progress summary */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="text-xs uppercase tracking-wider text-stone-500">Scores</div>
            <div
              className={`text-3xl font-semibold tabular-nums mt-1 ${
                allScoresComplete ? 'text-emerald-700' : 'text-stone-900'
              }`}
            >
              {filledScores} / {totalScores}
            </div>
            <div
              className={`mt-1 text-sm font-medium flex items-center gap-1 ${
                allScoresComplete ? 'text-emerald-700' : 'text-amber-700'
              }`}
            >
              <span className="material-symbols-rounded" style={{ fontSize: 16 }} aria-hidden="true">
                {allScoresComplete ? 'check_circle' : 'schedule'}
              </span>
              {allScoresComplete ? 'Complete' : 'Incomplete'}
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="text-xs uppercase tracking-wider text-stone-500">Evidence</div>
            <div className="text-3xl font-semibold tabular-nums mt-1 text-stone-900">
              {evidenceFields.filled} / {evidenceFields.total}
            </div>
            <div className="mt-1 text-sm text-stone-500">Optional — leaving blank is fine</div>
          </div>
        </div>

        {/* Reviewee context */}
        <div className="bg-stone-50 rounded-xl p-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-stone-200 text-stone-700 text-sm font-bold flex items-center justify-center shrink-0">
            {revieweeName
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map(s => s[0]?.toUpperCase())
              .join('') || '·'}
          </div>
          <div className="min-w-0">
            <div className="text-base font-medium text-stone-900 truncate">
              Reviewing {revieweeName}
            </div>
            <div className="text-sm text-stone-500 truncate">as {reviewerRoleLabel}</div>
          </div>
        </div>

        {/* Section-by-section summary */}
        <div>
          <h3 className="text-base font-semibold text-stone-900 mb-3">Your scores at a glance</h3>
          <ul className="space-y-2">
            {summaries.map(s => {
              const incomplete = s.filledLenses < s.totalLenses;
              return (
                <li
                  key={s.code}
                  className={[
                    'rounded-xl p-3 border',
                    incomplete ? 'bg-amber-50 border-amber-200' : 'bg-stone-50 border-stone-100',
                  ].join(' ')}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={[
                        'w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shrink-0',
                        incomplete ? 'bg-amber-200 text-amber-900' : 'bg-stone-900 text-white',
                      ].join(' ')}
                    >
                      {s.code}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-base font-medium text-stone-900 truncate">{s.title}</div>
                      <div className="text-sm text-stone-500 tabular-nums">
                        {s.avg !== null
                          ? `Avg ${s.avg.toFixed(1)} · ${s.filledLenses}/${s.totalLenses} lenses scored`
                          : `${s.filledLenses}/${s.totalLenses} lenses scored`}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1.5 mt-2 ml-10">
                    {LENS_KEYS.map(lens => (
                      <span
                        key={lens}
                        className="bg-white border border-stone-200 px-2 py-0.5 rounded text-xs font-medium text-stone-700 tabular-nums"
                      >
                        {OPTICS_LENS_LABELS[lens].split(' ')[0]}{' '}
                        {s.perLens[lens] !== null ? s.perLens[lens]!.toFixed(1) : '—'}
                      </span>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Self-narrative completeness */}
        {isSelf && narrativeFilled !== null && (
          <div className="bg-stone-50 rounded-xl p-3">
            <div className="text-xs uppercase tracking-wider text-stone-500">Section 7 narrative</div>
            <div className="text-base font-medium text-stone-900 mt-0.5 tabular-nums">
              {narrativeFilled} / 4 reflections written
            </div>
            <div className="text-sm text-stone-500 mt-0.5">
              All four are recommended but not required.
            </div>
          </div>
        )}

        {/* Anonymity reminder — anonymous reviewers only */}
        {!isAttributed && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <span
                className="material-symbols-rounded text-emerald-700"
                style={{ fontSize: 20 }}
                aria-hidden="true"
              >
                lock
              </span>
              <span className="text-base font-semibold text-emerald-900">
                Your identity stays private
              </span>
            </div>
            <p className="text-sm text-emerald-800 leading-relaxed">
              HR and {revieweeName.split(' ')[0]}'s line manager will see only the aggregated average
              — never your name or individual answers.
            </p>
          </div>
        )}

        {/* Attributed reviewer notice */}
        {isAttributed && !isSelf && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <span
                className="material-symbols-rounded text-blue-700"
                style={{ fontSize: 20 }}
                aria-hidden="true"
              >
                badge
              </span>
              <span className="text-base font-semibold text-blue-900">
                This is an attributed review
              </span>
            </div>
            <p className="text-sm text-blue-800 leading-relaxed">
              Your name <b>will</b> be visible to HR and the line manager. Your answers stay confidential
              outside that circle.
            </p>
          </div>
        )}
      </div>
    </BottomSheet>
  );
};

export default ConfirmReviewSheet;
